import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, assetUrl, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { LineChart, Line, YAxis, ResponsiveContainer } from "recharts";
import { Globe, Upload, Trash2, FileText, Loader2, Palette, PenLine, Link2, Sparkles, ArrowRight, AlertTriangle, Wand2, TrendingUp } from "lucide-react";

export default function BrandKB() {
  const { selected } = useBrand();
  const brandLabel = selected || "HWG Umbrella";
  const [url, setUrl] = useState("");
  const [crawling, setCrawling] = useState(false);
  const [kit, setKit] = useState(null);
  const [sources, setSources] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthTrend, setHealthTrend] = useState([]);
  const [uploadDelta, setUploadDelta] = useState(null);
  const [manual, setManual] = useState({ title: "", content: "" });
  const [ingesting, setIngesting] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [newRule, setNewRule] = useState({ name: "", pattern: "", brand: "", prompt_prefix: "" });
  const [addingRule, setAddingRule] = useState(false);
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get("/brand", { params: { brand: selected } }).then((r) => {
      setKit(r.data.kit);
      setSources(r.data.sources);
      setSites(r.data.sites || []);
    });
    api.get("/assets", { params: { brand: selected } }).then((r) => setAssets(r.data));
    api.get("/brands/health").then((r) => setHealth(r.data.find((h) => (h.brand || "") === selected) || null));
    api.get("/brands/health/trend", { params: { days: 30, brand: selected } })
      .then((r) => {
        const key = selected || "HWG Umbrella";
        setHealthTrend(r.data?.[key] || []);
      }).catch(() => setHealthTrend([]));
    api.get("/brands/health/last-upload-delta", { params: { brand: selected } })
      .then((r) => setUploadDelta(r.data && r.data.delta > 0 ? r.data : null))
      .catch(() => setUploadDelta(null));
    api.get("/brand/nudges", { params: { brand: selected } }).then((r) => setNudges(r.data || [])).catch(() => setNudges([]));
    api.get("/brand/suggestion-rules").then((r) => setRules(r.data || [])).catch(() => setRules([]));
  }, [selected]);
  useEffect(load, [load]);

  const crawl = async () => {
    if (!url.trim()) return toast.error("Enter your website URL");
    setCrawling(true);
    try {
      const { data } = await api.post("/brand/crawl", { url, brand: selected || null });
      setKit(data);
      setUrl("");
      api.get("/brand", { params: { brand: selected } }).then((r) => { setSources(r.data.sources); setSites(r.data.sites || []); });
      toast.success(`Website ingested into the ${brandLabel} knowledge base`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setCrawling(false);
    }
  };

  const removeSite = async (siteUrl) => {
    try {
      await api.delete("/brand/sites", { params: { url: siteUrl, brand: selected } });
      toast.success("Website removed from knowledge base");
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const removeSource = async (id) => {
    try {
      await api.delete(`/brand/knowledge/${id}`);
      setSources(sources.filter((s) => s.id !== id));
      toast.success("Source removed");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const ingest = async () => {
    if (!manual.title.trim() || !manual.content.trim()) return toast.error("Add a title and some content");
    setIngesting(true);
    try {
      const { data } = await api.post("/brand/ingest", { ...manual, brand: selected || null });
      if (data?.id) setKit(data);
      setManual({ title: "", content: "" });
      api.get("/brand", { params: { brand: selected } }).then((r) => { setSources(r.data.sources); setSites(r.data.sites || []); });
      toast.success(`Knowledge added to ${brandLabel}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setIngesting(false);
    }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    let pdfKnowledge = false;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("asset_type", f.type.startsWith("image") ? "image" : "document");
      fd.append("brand", selected || "");
      try {
        const { data } = await api.post("/assets", fd);
        if (data.knowledge_added) pdfKnowledge = true;
      } catch (err) {
        toast.error(errText(err));
      }
    }
    if (files.length) {
      toast.success(`${files.length} asset(s) uploaded to ${brandLabel}`);
      if (pdfKnowledge) {
        toast.success("PDF content extracted into the brand knowledge base");
        load();
      } else {
        api.get("/assets", { params: { brand: selected } }).then((r) => setAssets(r.data));
      }
    }
    e.target.value = "";
  };

  const pdfRef = useRef();
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { name, pct }

  // Chunked upload for a single PDF — bypasses the ~10MB proxy limit that the
  // Kubernetes ingress enforces. Uses 3MB chunks, streams to /assets/chunk/*
  // with a concurrency of 3 so huge PDFs finish ~3× faster than serial.
  const chunkedUploadPdf = async (file) => {
    const initFd = new FormData();
    initFd.append("filename", file.name);
    initFd.append("asset_type", "document");
    initFd.append("brand", selected || "");
    initFd.append("total_size", String(file.size));
    const { data: initData } = await api.post("/assets/chunk/init", initFd);
    const chunkSize = initData.chunk_size || 3 * 1024 * 1024;
    const totalChunks = initData.total_chunks || Math.ceil(file.size / chunkSize);
    const CONCURRENCY = 3;
    let completed = 0;
    setUploadProgress({ name: file.name, pct: 0 });
    // Simple worker-pool: each worker picks the next unclaimed chunk index.
    let nextIdx = 0;
    const uploadOne = async () => {
      while (true) {
        const i = nextIdx++;
        if (i >= totalChunks) return;
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        // Retry up to twice on network failure — a flaky wifi shouldn't
        // kill a 70-chunk upload of a 200MB brochure.
        let attempt = 0;
        while (true) {
          try {
            const fd = new FormData();
            fd.append("upload_id", initData.upload_id);
            fd.append("chunk_index", String(i));
            fd.append("chunk", blob, `chunk-${i}`);
            await api.post("/assets/chunk/append", fd);
            break;
          } catch (err) {
            attempt += 1;
            if (attempt > 2) throw err;
            await new Promise((r) => setTimeout(r, 500 * attempt));
          }
        }
        completed += 1;
        setUploadProgress({ name: file.name, pct: Math.round((completed / totalChunks) * 100) });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, totalChunks) }, uploadOne));
    const finalizeFd = new FormData();
    finalizeFd.append("upload_id", initData.upload_id);
    const { data } = await api.post("/assets/chunk/finalize", finalizeFd);
    return data;
  };

  const uploadPdfsForKnowledge = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (files.length === 0) {
      e.target.value = "";
      return toast.error("Please pick one or more .pdf files");
    }
    setUploadingPdf(true);
    let uploaded = 0;
    let queued = 0;
    const CHUNK_THRESHOLD = 3 * 1024 * 1024; // 3MB
    for (const f of files) {
      try {
        let data;
        if (f.size > CHUNK_THRESHOLD) {
          setUploadProgress({ name: f.name, pct: 0 });
          data = await chunkedUploadPdf(f);
        } else {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("asset_type", "document");
          fd.append("brand", selected || "");
          const resp = await api.post("/assets", fd);
          data = resp.data;
        }
        uploaded += 1;
        if (data.status === "processing") queued += 1;
      } catch (err) {
        toast.error(`${f.name}: ${errText(err)}`);
      }
    }
    setUploadingPdf(false);
    setUploadProgress(null);
    e.target.value = "";
    if (uploaded) {
      if (queued) {
        toast.info(`${uploaded} PDF${uploaded !== 1 ? "s" : ""} uploaded — ${queued} processing in the background. We'll flag any failures here.`);
      } else {
        toast.success(`${uploaded} PDF${uploaded !== 1 ? "s" : ""} uploaded`);
      }
      load();
    }
  };

  const pdfSources = (sources || []).filter((s) => (s.type || "").toLowerCase() === "pdf");
  // Assets that are still processing (or failed) but have no knowledge doc yet —
  // surface them at the top of the PDF list so the user sees a live spinner.
  const processingPdfAssets = (assets || []).filter((a) => {
    const isPdf = (a.filename || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) return false;
    if (a.status === "processing" || a.status === "failed") return true;
    return false;
  });

  // Session-hydrated failure watcher + processing-toast lifecycle. Any newly
  // failed PDF fires a persistent error toast; any newly processing PDF fires
  // a persistent "Still processing…" info toast that is dismissed once the
  // asset flips to ready or failed. Together these guarantee the user always
  // knows exactly where their upload is even for a 30-minute 205MB brochure.
  const notifiedFailuresRef = useRef(new Set());
  const processingToastIdsRef = useRef(new Map()); // asset.id → toast id
  const failureHydratedRef = useRef(false);
  useEffect(() => {
    if (!assets) return;
    // Hydrate the "already-notified-failure" set on first render so page
    // refreshes don't re-toast pre-existing failures.
    if (!failureHydratedRef.current) {
      for (const a of assets) {
        if (a.status === "failed" && (a.filename || "").toLowerCase().endsWith(".pdf")) {
          notifiedFailuresRef.current.add(a.id);
        }
      }
      failureHydratedRef.current = true;
    }
    // Processing toasts + fresh-failure toasts run every render — the
    // hydration guard only suppresses re-firing OLD failure toasts.
    for (const a of assets) {
      const isPdf = (a.filename || "").toLowerCase().endsWith(".pdf");
      if (!isPdf) continue;
      if (a.status === "processing" && !processingToastIdsRef.current.has(a.id)) {
        const tid = toast.info(`Still processing ${a.filename}…`, {
          duration: Infinity,
          description: "We'll flag any failures or refresh when it's ready.",
        });
        processingToastIdsRef.current.set(a.id, tid);
      }
      if (a.status !== "processing" && processingToastIdsRef.current.has(a.id)) {
        const tid = processingToastIdsRef.current.get(a.id);
        try { toast.dismiss(tid); } catch { /* ignore */ }
        processingToastIdsRef.current.delete(a.id);
      }
      if (a.status === "failed" && !notifiedFailuresRef.current.has(a.id)) {
        notifiedFailuresRef.current.add(a.id);
        toast.error(`${a.filename}: ${a.error || "PDF processing failed"}`);
      }
    }
  }, [assets]);

  useEffect(() => {
    const anyProcessing = (assets || []).some((a) =>
      (a.filename || "").toLowerCase().endsWith(".pdf") && a.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(() => {
      api.get("/assets", { params: { brand: selected } }).then((r) => {
        const next = r.data;
        setAssets(next);
        const stillProcessing = next.some((a) =>
          (a.filename || "").toLowerCase().endsWith(".pdf") && a.status === "processing");
        if (!stillProcessing) {
          // Everything finished — refresh sources + nudges to reflect new KB state.
          load();
        }
      });
    }, 3000);
    return () => clearInterval(t);
  }, [assets, selected, load]);

  const openPdfPreview = async (kid) => {
    setPreview({ kid, loading: true });
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/brand/knowledge/${kid}/preview`);
      setPreview({ kid, ...data });
    } catch (err) {
      toast.error(errText(err));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyNudge = async (id, type) => {
    try {
      await api.post(`/brand/nudges/${id}/apply`);
      toast.success(type === "conflict" ? "Conflict acknowledged" : "Applied to team kit description");
      setNudges(nudges.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const dismissNudge = async (id) => {
    try {
      await api.post(`/brand/nudges/${id}/dismiss`);
      setNudges(nudges.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const toggleRule = async (rule) => {
    try {
      const { data } = await api.patch(`/brand/suggestion-rules/${rule.id}`, { active: !rule.active });
      setRules(rules.map((r) => r.id === rule.id ? { ...r, active: data.active } : r));
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Delete this KB suggestion rule? Existing nudges from it will stay.")) return;
    try {
      await api.delete(`/brand/suggestion-rules/${id}`);
      setRules(rules.filter((r) => r.id !== id));
      toast.success("Rule deleted");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const addRule = async () => {
    if (!newRule.name.trim() || !newRule.pattern.trim()) {
      return toast.error("Name and regex pattern are required");
    }
    try {
      new RegExp(newRule.pattern);
    } catch {
      return toast.error("Invalid regex pattern");
    }
    setAddingRule(true);
    try {
      const { data } = await api.post("/brand/suggestion-rules", {
        name: newRule.name.trim(),
        pattern: newRule.pattern,
        brand: newRule.brand.trim() || null,
        prompt_prefix: newRule.prompt_prefix.trim() || null,
        field: "team_kit_description",
        nudge_type: "team_kit",
        active: true,
      });
      setRules([...rules, data]);
      setNewRule({ name: "", pattern: "", brand: "", prompt_prefix: "" });
      toast.success("Rule added — will fire on the next PDF ingest");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setAddingRule(false);
    }
  };

  return (
    <div className="space-y-8 fade-up" data-testid="brand-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Brand Knowledge Base</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Teach the AI your brand.</h1>
        </div>
        <Badge variant="secondary" className="text-sm font-medium px-3 py-1" data-testid="kb-brand-context-badge">
          Managing: {brandLabel}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground -mt-4">Everything on this page is scoped to <span className="font-semibold text-foreground">{brandLabel}</span>. Use the brand selector at the top to switch brands.</p>

      {nudges.length > 0 && (
        <div className="space-y-3" data-testid="kb-nudges-list">
          {nudges.map((n) => {
            const isConflict = n.type === "conflict";
            return (
              <Card key={n.id} className={`${isConflict ? "border-amber-500/50 bg-amber-500/5" : "border-accent/40 bg-accent/5"}`} data-testid={`kb-nudge-${n.id}`}>
                <CardContent className="p-5 flex flex-wrap items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isConflict ? "bg-amber-500/15 text-amber-600" : "bg-accent/15 text-accent"}`}>
                    {isConflict ? <AlertTriangle size={18} /> : <Sparkles size={18} />}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {isConflict ? (
                      <>
                        <p className="text-xs uppercase tracking-widest text-amber-600 font-semibold">
                          KB conflict · {n.field || "brand"} · from {n.source_filename || "PDF"}
                        </p>
                        <p className="text-sm leading-relaxed">
                          The new PDF disagrees with existing website content on <span className="font-semibold">{n.field || "this field"}</span>{n.brand ? ` for ${n.brand}` : ""}.
                        </p>
                        <div className="text-xs space-y-1.5 mt-2">
                          <div className="flex gap-2"><Badge variant="outline" className="shrink-0 h-5 text-[10px]">Website</Badge><span className="text-muted-foreground italic">"{n.website_says}"</span></div>
                          <div className="flex gap-2"><Badge variant="outline" className="shrink-0 h-5 text-[10px]">PDF</Badge><span className="text-muted-foreground italic">"{n.pdf_says}"</span></div>
                        </div>
                        {n.suggested_text && (
                          <p className="text-xs pt-1"><span className="font-semibold">Recommendation:</span> {n.suggested_text}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-xs uppercase tracking-widest text-accent font-semibold">
                          Team kit suggestion · from {n.source_filename || "PDF"}
                        </p>
                        <p className="text-sm leading-relaxed">
                          {n.prompt_prefix || "We spotted a match."} Update{n.brand ? ` ${n.brand}'s` : " the brand's"} team kit description to:
                        </p>
                        <blockquote className="text-sm border-l-2 border-accent pl-3 italic text-muted-foreground">
                          "{n.suggested_text}"
                        </blockquote>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" onClick={() => applyNudge(n.id, n.type)} data-testid={`kb-nudge-apply-${n.id}`}>
                      {isConflict ? "Acknowledge" : "Apply"} <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dismissNudge(n.id)} data-testid={`kb-nudge-dismiss-${n.id}`}>
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {health && (
        <Card data-testid="kb-health-card">
          <CardContent className="p-5 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center shrink-0 ${health.score >= 75 ? "border-accent" : health.score >= 40 ? "border-amber-500" : "border-destructive"}`}>
                <span className="font-heading text-xl font-semibold" data-testid="kb-health-score">{health.score}</span>
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm">Knowledge health</p>
                  {uploadDelta && (
                    <span
                      data-testid="kb-health-upload-delta"
                      title={uploadDelta.uploaded_at ? `Since ${new Date(uploadDelta.uploaded_at).toLocaleDateString()}` : ""}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-accent/15 text-accent"
                    >
                      <TrendingUp size={11} /> +{uploadDelta.delta} pts from your last upload
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{health.counts.sites} website{health.counts.sites !== 1 ? "s" : ""} · {health.counts.pdfs} PDF{health.counts.pdfs !== 1 ? "s" : ""} · {health.counts.notes} note{health.counts.notes !== 1 ? "s" : ""}</p>
              </div>
            </div>
            {healthTrend.length > 1 && (
              <div className="h-12 w-32 shrink-0" data-testid="kb-health-chip-sparkline">
                <ResponsiveContainer width="99%" height="100%">
                  <LineChart data={healthTrend} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <YAxis hide domain={[0, 100]} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {health.tips.length > 0 ? (
              <ul className="text-xs text-muted-foreground space-y-1 flex-1 min-w-[240px]" data-testid="kb-health-tips">
                {health.tips.map((t, i) => <li key={i} className="flex gap-1.5"><span className="text-accent">→</span> {t}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-accent font-semibold">Complete — the AI has everything it needs for {brandLabel}.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Globe size={18} /> Website crawler</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">We'll crawl the homepage, /about, /products, /services, /blog and /pricing — extracting mission, voice, products, colors and imagery to ground all AI content for {brandLabel}. Add as many websites as you need.</p>
          <div className="flex gap-3 flex-wrap">
            <Input data-testid="crawl-url-input" className="max-w-md" placeholder="yourbrand.com" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && crawl()} />
            <Button data-testid="crawl-btn" onClick={crawl} disabled={crawling}>
              {crawling ? <><Loader2 size={14} className="mr-2 animate-spin" /> Crawling & analyzing...</> : `Crawl for ${brandLabel}`}
            </Button>
          </div>
          <div data-testid="crawled-sites-list">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Crawled websites ({sites.length})</p>
            {sites.length === 0 ? (
              <p className="text-sm text-muted-foreground">No websites crawled for {brandLabel} yet.</p>
            ) : (
              <div className="space-y-2">
                {sites.map((s) => (
                  <div key={s.url} data-testid={`crawled-site-${s.url}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 size={14} className="text-accent shrink-0" />
                      <a href={s.url} target="_blank" rel="noreferrer" className="truncate hover:underline">{s.url}</a>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{s.pages} page{s.pages !== 1 ? "s" : ""}</span>
                      {s.crawled_at && <span className="text-xs text-muted-foreground hidden sm:inline">{new Date(s.crawled_at).toLocaleDateString()}</span>}
                      <button data-testid={`delete-site-btn-${s.url}`} onClick={() => removeSite(s.url)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Remove this website">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Site built with a JavaScript page builder (GoDaddy, Wix)? Some content may not be readable — use "Add knowledge manually" below to paste it in.</p>
        </CardContent>
      </Card>

      <Card data-testid="pdf-upload-card">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><FileText size={18} /> Upload brand PDFs</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Drop in brand guidelines, product one-pagers, style guides, transcripts — any PDF. We'll extract the text and index it into the {brandLabel} knowledge base so every AI post can draw from it.</p>
          <input ref={pdfRef} type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={uploadPdfsForKnowledge} data-testid="kb-pdf-input" />
          <Button data-testid="kb-upload-pdf-btn" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf}>
            {uploadingPdf ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</> : <><Upload size={14} className="mr-2" /> Upload PDFs</>}
          </Button>
          {uploadProgress && (
            <div className="text-xs text-muted-foreground" data-testid="kb-pdf-upload-progress">
              <div className="flex justify-between mb-1"><span className="truncate">{uploadProgress.name}</span><span>{uploadProgress.pct}%</span></div>
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${uploadProgress.pct}%` }} />
              </div>
            </div>
          )}
          <div data-testid="kb-pdf-list">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-4 mb-2">Uploaded PDFs ({pdfSources.length + processingPdfAssets.length})</p>
            {(pdfSources.length + processingPdfAssets.length) === 0 ? (
              <p className="text-sm text-muted-foreground">No PDFs uploaded for {brandLabel} yet.</p>
            ) : (
              <div className="space-y-2">
                {processingPdfAssets.map((a) => (
                  <div key={a.id} data-testid={`kb-pdf-processing-${a.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/70 text-sm border border-dashed border-border">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText size={14} className="text-muted-foreground shrink-0" />
                      <span className="truncate" title={a.filename}>{a.filename}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {a.status === "failed" ? (
                        <Badge variant="destructive" className="text-[10px]" data-testid={`kb-pdf-status-failed-${a.id}`}>Failed</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] gap-1" data-testid={`kb-pdf-status-processing-${a.id}`}>
                          <Loader2 size={10} className="animate-spin" /> Processing…
                        </Badge>
                      )}
                      {a.status === "failed" && (
                        <button
                          data-testid={`kb-pdf-remove-failed-${a.id}`}
                          onClick={async () => {
                            try {
                              await api.delete(`/assets/${a.id}`);
                              setAssets(assets.filter((x) => x.id !== a.id));
                            } catch (err) { toast.error(errText(err)); }
                          }}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          title="Remove failed upload"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {pdfSources.map((s) => (
                  <div key={s.id} data-testid={`kb-pdf-source-${s.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted text-sm hover:bg-muted/80 transition-colors">
                    <button
                      type="button"
                      onClick={() => openPdfPreview(s.id)}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      data-testid={`kb-pdf-open-${s.id}`}
                    >
                      <FileText size={14} className="text-accent shrink-0" />
                      <span className="truncate hover:underline" title={s.title}>{s.title || s.source}</span>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      {s.created_at && <span className="text-xs text-muted-foreground hidden sm:inline">{new Date(s.created_at).toLocaleDateString()}</span>}
                      <button data-testid={`kb-pdf-delete-btn-${s.id}`} onClick={() => removeSource(s.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Remove this PDF from the knowledge base">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><PenLine size={18} /> Add knowledge manually</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Paste service descriptions, brand story, pricing or anything the AI should know about {brandLabel}.</p>
          <Input data-testid="ingest-title-input" className="max-w-md" placeholder="Title, e.g. 'Our services'" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} />
          <Textarea data-testid="ingest-content-input" rows={5} placeholder="Paste brand content here..." value={manual.content} onChange={(e) => setManual({ ...manual, content: e.target.value })} />
          <Button data-testid="ingest-btn" onClick={ingest} disabled={ingesting}>
            {ingesting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analyzing...</> : "Add to knowledge base"}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="kb-rules-card">
        <CardHeader className="cursor-pointer" onClick={() => setRulesOpen(!rulesOpen)}>
          <CardTitle className="font-heading text-xl flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Wand2 size={18} /> Auto-nudge rules <Badge variant="outline" className="text-[10px] ml-1" data-testid="kb-rules-count">{rules.length}</Badge></span>
            <Button size="sm" variant="ghost" data-testid="kb-rules-toggle" onClick={(e) => { e.stopPropagation(); setRulesOpen(!rulesOpen); }}>
              {rulesOpen ? "Hide" : "Manage"}
            </Button>
          </CardTitle>
        </CardHeader>
        {rulesOpen && (
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              When a PDF is ingested we scan it against these regex rules. Any match becomes a nudge card at the top of this page — click Apply to update the target sub-brand's team kit description.
            </p>
            {rules.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="kb-rules-empty">No rules configured yet. Add one below to auto-flag brand mentions in future PDFs.</p>
            ) : (
              <div className="space-y-2" data-testid="kb-rules-list">
                {rules.map((r) => (
                  <div key={r.id} data-testid={`kb-rule-${r.id}`} className="p-3 rounded-lg bg-muted text-sm flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{r.name}</span>
                        {r.brand && <Badge variant="outline" className="text-[10px]">brand: {r.brand}</Badge>}
                        {r.seeded && <Badge variant="secondary" className="text-[10px]">default</Badge>}
                        {!r.active && <Badge variant="outline" className="text-[10px] text-muted-foreground">off</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground font-mono truncate" title={r.pattern}>/{r.pattern}/i → {r.field || "team_kit_description"}</p>
                      {r.prompt_prefix && <p className="text-xs text-muted-foreground italic">"{r.prompt_prefix}"</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => toggleRule(r)} data-testid={`kb-rule-toggle-${r.id}`}>
                        {r.active ? "Turn off" : "Turn on"}
                      </Button>
                      <button data-testid={`kb-rule-delete-${r.id}`} onClick={() => deleteRule(r.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" title="Delete rule">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="pt-3 border-t border-border space-y-2.5" data-testid="kb-rule-add-form">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Add a new rule</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Input data-testid="kb-rule-name" placeholder="Rule name (e.g. 'Amelia signature')" value={newRule.name} onChange={(e) => setNewRule({ ...newRule, name: e.target.value })} />
                <Input data-testid="kb-rule-brand" placeholder="Sub-brand (blank = all brands)" value={newRule.brand} onChange={(e) => setNewRule({ ...newRule, brand: e.target.value })} />
              </div>
              <Input data-testid="kb-rule-pattern" className="font-mono text-sm" placeholder="Regex pattern (e.g. ([^.!?\\n]*amelia's\\s+signature[^.!?\\n]*[.!?]))" value={newRule.pattern} onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })} />
              <Input data-testid="kb-rule-prefix" placeholder="Nudge blurb (e.g. 'We spotted Amelia's signature.')" value={newRule.prompt_prefix} onChange={(e) => setNewRule({ ...newRule, prompt_prefix: e.target.value })} />
              <Button size="sm" onClick={addRule} disabled={addingRule} data-testid="kb-rule-add-btn">
                {addingRule ? <><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</> : "Add rule"}
              </Button>
              <p className="text-[11px] text-muted-foreground">Wrap the phrase you want to capture in a capture group so we can extract the full sentence. Matching is case-insensitive.</p>
            </div>
          </CardContent>
        )}
      </Card>

      {kit && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="brand-kit-section">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-heading text-xl">{kit.brand_name || brandLabel}</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {kit.mission && <div><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Mission</p><p className="text-sm leading-relaxed">{kit.mission}</p></div>}
              {kit.tone && <div><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">Voice & tone</p><p className="text-sm leading-relaxed">{kit.tone}</p></div>}
              {kit.value_props?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Value propositions</p>
                  <div className="flex flex-wrap gap-2">{kit.value_props.map((v, i) => <Badge key={i} variant="secondary" className="font-normal">{v}</Badge>)}</div>
                </div>
              )}
              {kit.products?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Products & services</p>
                  <div className="space-y-2">
                    {kit.products.slice(0, 6).map((p, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted text-sm">
                        <span className="font-semibold">{p.name}</span>{p.price ? <span className="text-accent font-semibold ml-2">{p.price}</span> : null}
                        <p className="text-muted-foreground text-xs mt-1">{p.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Palette size={16} /> Brand colors</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(kit.colors || []).map((c) => (
                    <div key={c} className="text-center">
                      <div className="w-10 h-10 rounded-lg border border-border" style={{ background: c }} />
                      <p className="text-[9px] text-muted-foreground mt-1">{c}</p>
                    </div>
                  ))}
                  {(!kit.colors || !kit.colors.length) && <p className="text-sm text-muted-foreground">None detected</p>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><FileText size={16} /> Knowledge sources</CardTitle></CardHeader>
              <CardContent className="space-y-2" data-testid="knowledge-sources-list">
                {sources.length === 0 && <p className="text-sm text-muted-foreground">No sources yet.</p>}
                {sources.map((s) => (
                  <div key={s.id} className="text-xs p-2 rounded bg-muted flex items-center justify-between gap-2" title={s.source} data-testid={`knowledge-source-${s.id}`}>
                    <span className="truncate">{s.source}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[9px]">{s.type || "url"}</Badge>
                      <button data-testid={`delete-source-btn-${s.id}`} onClick={() => removeSource(s.id)} className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors" title="Remove source">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {kit?.images?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-xl">On-site imagery</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {kit.images.slice(0, 12).map((img, i) => (
                <img key={i} src={img} alt="" className="w-full aspect-square object-cover rounded-lg border border-border" onError={(e) => { e.target.style.display = "none"; }} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="dam-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl flex items-center gap-2"><Upload size={18} /> Digital Asset Manager <Badge variant="outline" className="ml-1 font-normal">{brandLabel}</Badge></CardTitle>
          <Button variant="outline" data-testid="upload-asset-btn" onClick={() => fileRef.current?.click()}><Upload size={14} className="mr-2" /> Upload assets</Button>
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.ttf,.otf,.woff" className="hidden" onChange={upload} data-testid="asset-file-input" />
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Upload logos, product photography, fonts and brand guideline PDFs for {brandLabel}. PDFs are also extracted into the knowledge base.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              {assets.map((a) => (
                <div key={a.id} className="group relative rounded-lg border border-border overflow-hidden bg-muted" data-testid={`asset-${a.id}`}>
                  {a.asset_type === "image" ? (
                    <img src={assetUrl(a.url)} alt={a.filename} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square flex flex-col items-center justify-center gap-2 text-muted-foreground"><FileText size={24} /><span className="text-[10px] px-2 truncate max-w-full">{a.filename}</span></div>
                  )}
                  <button data-testid={`delete-asset-${a.id}`} onClick={() => api.delete(`/assets/${a.id}`).then(() => setAssets(assets.filter((x) => x.id !== a.id)))}
                    className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="kb-pdf-preview-drawer">
          <SheetHeader>
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText size={18} className="text-accent" /> {preview?.source?.title || "PDF preview"}
            </SheetTitle>
            <SheetDescription>
              {preview?.source?.created_at ? `Ingested ${new Date(preview.source.created_at).toLocaleDateString()}` : "Extracted text preview"}
              {preview?.source?.brand ? ` · ${preview.source.brand}` : ""}
              {preview?.source?.ocr_used && (
                <Badge variant="secondary" className="text-[10px] ml-2" data-testid="kb-pdf-preview-ocr-pill">
                  OCR-recovered
                </Badge>
              )}
            </SheetDescription>
          </SheetHeader>
          {previewLoading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading preview…
            </div>
          ) : preview && (
            <div className="mt-6 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Extracted text preview</p>
                <div className="p-3 rounded-lg bg-muted text-sm leading-relaxed max-h-72 overflow-y-auto whitespace-pre-wrap" data-testid="kb-pdf-preview-text">
                  {preview.preview || <span className="text-muted-foreground italic">No text extracted.</span>}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Referenced in {preview.post_count || 0} post{preview.post_count === 1 ? "" : "s"} since ingestion
                </p>
                {(!preview.posts || preview.posts.length === 0) ? (
                  <p className="text-sm text-muted-foreground" data-testid="kb-pdf-preview-empty-posts">
                    Not referenced yet — new batches will pick this up automatically.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="kb-pdf-preview-posts">
                    {preview.posts.map((p) => (
                      <div key={p.id} className="p-2.5 rounded-lg bg-muted text-xs flex items-center justify-between gap-2" data-testid={`kb-pdf-preview-post-${p.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{p.title || "(untitled)"}</p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {p.brand || "HWG"} · {p.platform || "post"} · {p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString() : "unscheduled"}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">{p.review_status || "pending"}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
